<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import Star from '$lib/components/Star.svelte';
  import ContactCtaSection from '$lib/sections/ContactCtaSection.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';

  const reasons = [
    { label: 'Multi-tenant architecture', description: 'One foundation, many clients, each in their own cloud' },
    { label: 'Extensible via API, MCP, OpenAPI', description: 'Build your proprietary layer on top' },
    { label: 'White-label ready', description: 'Your brand, your client relationship' },
    { label: 'Repeatable deployments', description: 'Stop rebuilding the foundation for every engagement' },
    { label: 'Built-in governance', description: 'The compliance conversation is already handled' },
  ];
</script>

<PageMeta
  title="Service Providers & SIs — Your clients need AI"
  description="Corral is the 80% foundation. You deliver the 20% that wins the deal. Your IP, their tenant, your margin."
/>

<!-- Top stars -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <p class="font-mono text-[14px] text-[#ee6018] mb-4">Service Providers & SIs</p>
        <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
          Your clients need AI. You need a foundation that scales with your practice.
        </h1>
        <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
          Your clients are asking for AI. Every one of them. The build-from-scratch approach doesn't scale — it's a custom engagement every time, and you're rebuilding the same foundation for each client.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §1: The pitch -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-8">
          We're the 80%. You deliver the 20% that wins the deal.
        </h2>
        <div class="flex flex-col gap-6">
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            Deploy Corral into each client's tenant. Extend it with your own IP. The custom business logic, the domain expertise, the client-specific integrations — that's your value. The foundation is ours to maintain.
          </p>
        </div>

        <div class="my-10 border-l-2 border-[#ee6018] pl-6 py-2">
          <p class="font-mono font-light text-[18px] text-white leading-relaxed">
            Your code, their cloud, your margin.
          </p>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
  <Star variant="full" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="full" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §2: Why SIs choose Corral -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-3 block">Why SIs choose Corral</span>
        <div class="flex flex-col gap-0">
          {#each reasons as reason, i}
            <div class="flex items-start gap-4 py-5 {i < reasons.length - 1 ? 'border-b border-[#333335]' : ''}">
              <span class="w-1.5 h-1.5 bg-[#ee6018] rounded-full mt-2 shrink-0"></span>
              <div>
                <span class="font-sans text-[16px] text-white">{reason.label}</span>
                <span class="font-mono font-light text-[14px] text-[#878787] ml-2">— {reason.description}</span>
              </div>
            </div>
          {/each}
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider: → CTA -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- CTA -->
<ContactCtaSection />
